import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Minus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useChatStore } from '@/store/chatStore';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTeamConversation } from '@/hooks/useTeamConversation';
import { useMyConversations } from '@/hooks/useMyConversations';
import { useInquiries } from '@/hooks/useInquiries';
import { useNotifications, useMarkAsRead } from '@/hooks/useNotifications';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';
import { handleNotificationClick } from '@/lib/notificationRouting';
import { supabase } from '@/integrations/supabase/client';
import { ChatInboxList } from './ChatInboxList';
import { ChatThread } from './ChatThread';
import { PreRegisterChatThread } from './PreRegisterChatThread';
import { P2PThread } from './P2PThread';
import { InquiryThread } from './InquiryThread';
import { ContactMessager } from '@/components/contact/ContactMessager';
import teamAvatar from '@/assets/cardsimp-team-avatar.png';

/**
 * Chat surface.
 * - Desktop: floating bottom-right panel with X/minimize chrome.
 * - Mobile: full-width bottom drawer (no X/minimize — swipe-down or backdrop to dismiss).
 */
export const ChatPanel = () => {
	const { isOpen, isMinimized, view, minimize, restore, close, openTeamThread, goInbox, activeConversationId, setActiveConversation, activeInquiryId, setActiveInquiry } = useChatStore();
	const { user } = useAuth();
	const isMobile = useIsMobile();
	const location = useLocation();
	const isLandingVariant = location.pathname.startsWith('/landing');
	const isPreRegister = location.pathname === '/';
	// Lift the floating button above the PreRegister footer (divider + nav + wordmark + year).
	const bottomOffset = isPreRegister ? 'calc(env(safe-area-inset-bottom) + 148px)' : 'calc(env(safe-area-inset-bottom) + 16px)';
	// Universal team thread = the canonical ChatThread with subject pills + attachments
	// (was previously gated to /landing). PreRegisterChatThread is kept around for the
	// stripped public `/` surface where it's mounted directly, not via ChatPanel.
	void isLandingVariant;
	const TeamThreadComponent = ChatThread;
	const { conversationId, messages, markRead, onMessageSent, unread } = useTeamConversation();
	const { conversations, unreadCount: dmUnread, markRead: markDmRead } = useMyConversations();
	const { data: inquiries = [] } = useInquiries();
	const inquiryUnread = useMemo(() => inquiries.filter((i) => !i.read_at).length, [inquiries]);
	const { data: notifications = [] } = useNotifications();
	const notifUnread = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);
	const markNotifRead = useMarkAsRead();
	const navigate = useNavigate();

	// Resolve the active DM (if any) from the loaded conversations list.
	const activeDm = useMemo(
		() =>
			activeConversationId && activeConversationId !== 'team'
				? conversations.find((c) => c.id === activeConversationId) ?? null
				: null,
		[activeConversationId, conversations]
	);

	const activeInquiry = useMemo(
		() => (activeInquiryId ? inquiries.find((i) => i.id === activeInquiryId) ?? null : null),
		[activeInquiryId, inquiries]
	);

	const teamLastMessageAt = messages[messages.length - 1]?.created_at ?? null;

	// One-shot fetch for an inquiry id that isn't already in the local list.
	const fetchInquiryOnce = useCallback(async (id: string) => {
		const { data } = await supabase
			.from('inquiries')
			.select('*')
			.eq('id', id)
			.maybeSingle();
		return data;
	}, []);

	// Deep-link: ?inquiry=<id> opens the chat panel straight into that inquiry,
	// then strips the param so it doesn't re-trigger.
	const { open: openChat } = useChatStore();
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const inquiryId = params.get('inquiry');
		if (!inquiryId) return;

		const stripParam = () => {
			params.delete('inquiry');
			const qs = params.toString();
			navigate(`${location.pathname}${qs ? `?${qs}` : ''}${location.hash}`, { replace: true });
		};

		const existing = inquiries.find((i) => i.id === inquiryId);
		if (existing) {
			openChat({ inquiryId });
			stripParam();
			return;
		}
		// Not in local list yet — fetch by id, only strip the URL if we succeed
		// so the effect can retry after `useInquiries` hydrates.
		let cancelled = false;
		fetchInquiryOnce(inquiryId).then((row) => {
			if (cancelled) return;
			if (row) {
				openChat({ inquiryId });
				stripParam();
			}
		});
		return () => {
			cancelled = true;
		};
	}, [location.pathname, location.search, location.hash, openChat, navigate, inquiries, fetchInquiryOnce]);


	useEffect(() => {
		if (!isOpen || isMinimized) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') close();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [isOpen, isMinimized, close]);

	// Mark read when entering thread view
	useEffect(() => {
		if (!isOpen || isMinimized || view !== 'thread') return;
		if (activeConversationId === 'team') {
			void markRead();
		} else if (activeConversationId) {
			void markDmRead(activeConversationId);
		}
	}, [isOpen, isMinimized, view, activeConversationId, markRead, markDmRead, messages.length]);

	if (!isOpen) return null;

	// Mobile: drawer doesn't minimize — treat minimize state as closed.
	if (isMinimized && isMobile) {
		return null;
	}

	const totalUnread = (unread ? 1 : 0) + dmUnread + inquiryUnread + notifUnread;

	if (isMinimized) {
		return (
			<button
				type="button"
				onClick={restore}
				className="fixed z-[60] inline-flex items-center gap-2.5 h-12 pl-1.5 pr-4 rounded-full bg-chrome/95 border border-white/[0.08] shadow-2xl shadow-black/40 hover:border-primary/40 transition-colors animate-fade-in"
				style={{
					bottom: bottomOffset,
					right: 'calc(env(safe-area-inset-right) + 16px)',
				}}
				aria-label={totalUnread > 0 ? `Open chat, ${totalUnread} unread` : 'Open chat'}
			>
				<span
					className="relative h-9 w-9 rounded-full overflow-hidden bg-[hsl(var(--active-gold)/0.15)] shrink-0"
					style={{ boxShadow: '0 0 0 1px hsl(var(--active-gold) / 0.3)' }}
				>
					<img
						src={teamAvatar}
						alt=""
						className="absolute inset-0 w-full h-full object-cover scale-[1.35] translate-y-[6%]"
					/>
				</span>
				<span className="text-[13px] font-medium text-foreground">Chat</span>
				{totalUnread > 0 && (
					<span className="inline-flex items-center justify-center h-5 min-w-[20px] rounded-full px-1.5 text-[10.5px] font-black leading-none bg-active-gold text-[#121416] shadow-[0_2px_8px_hsl(var(--active-gold)/0.35)] tabular-nums">
						{totalUnread > 99 ? '99+' : totalUnread}
					</span>
				)}
			</button>
		);
	}

	const chromeButtons = isMobile ? null : (
		<div className="absolute top-1.5 right-1.5 z-10 flex items-center gap-0.5">
			<button
				type="button"
				onClick={minimize}
				aria-label="Minimize"
				className="h-8 w-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-white/[0.05] transition-colors"
			>
				<Minus className="h-4 w-4" />
			</button>
			<button
				type="button"
				onClick={close}
				aria-label="Close"
				className="h-8 w-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-white/[0.05] transition-colors"
			>
				<X className="h-4 w-4" />
			</button>
		</div>
	);

  const body = (
		<>
			{/* Desktop chrome floats over content (absolute) so it doesn't reserve an empty bar above the header. */}
			{user && !isMobile && chromeButtons}
			<div className="flex-1 min-h-0 flex flex-col">
				{!user ? (
					<ContactMessager headerSlot={chromeButtons} />
				) : view === 'inbox' ? (
					<ChatInboxList
						onOpenTeam={() => openTeamThread()}
						onOpenDM={(c) => setActiveConversation(c.id)}
						markDmRead={markDmRead}
						onOpenInquiry={(inq) => setActiveInquiry(inq.id)}
						onOpenNotification={(n) =>
							handleNotificationClick(n, {
								navigate,
								isMobile,
								markRead: (id) => markNotifRead.mutate(id),
							})
						}
						teamPreview={messages.length > 0 ? messages[messages.length - 1].body : null}
						teamUnread={unread}
						teamLastMessageAt={teamLastMessageAt}
						conversations={conversations}
						inquiries={inquiries}
						notifications={notifications}
					/>
				) : view === 'inquiry' ? (
					activeInquiry ? (
						<InquiryThread inquiry={activeInquiry} onBack={goInbox} />
					) : (
						<div className="flex-1 grid place-items-center text-[12px] text-muted-foreground">
							Loading inquiry…
						</div>
					)
				) : activeConversationId === 'team' || !activeConversationId ? (
					<TeamThreadComponent
						onBack={goInbox}
						conversationId={conversationId}
						messages={messages}
						onMessageSent={onMessageSent}
					/>
				) : activeDm ? (
					<P2PThread
						conversationId={activeDm.id}
						otherUserId={activeDm.other_user_id}
						otherUsername={activeDm.other_username}
						otherAvatarUrl={activeDm.other_avatar_url}
						onBack={goInbox}
					/>
				) : (
					<div className="flex-1 grid place-items-center text-[12px] text-muted-foreground">
						Loading conversation…
					</div>
				)}
			</div>
		</>
	);

	if (isMobile) {
		return <MobileChatDrawer onClose={close} body={body} />;
	}

	return (
		<>
			{/* Soft halo scrim so the panel reads cleanly over busy product imagery. */}
			<div
				aria-hidden
				className="fixed bottom-0 right-0 z-[59] w-[480px] h-[360px] pointer-events-none animate-in fade-in duration-200"
				style={{
					background:
						'radial-gradient(ellipse at bottom right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0) 75%)',
				}}
			/>
			<div
				role="dialog"
				aria-label="Chat"
				className={cn(
					'fixed bottom-0 right-0 z-[60] max-w-[100vw]',
					'w-[360px] h-[68dvh] max-h-[68dvh]',
					'rounded-tl-2xl overflow-hidden flex flex-col',
					'bg-chrome border border-white/[0.08]',
					'shadow-[0_2px_8px_rgba(0,0,0,0.35),0_24px_60px_-12px_rgba(0,0,0,0.65)]',
					'animate-in slide-in-from-bottom-4 fade-in duration-200'
				)}
				style={{
					paddingBottom: 'env(safe-area-inset-bottom)',
					paddingRight: 'env(safe-area-inset-right)',
					boxShadow:
						'0 2px 8px rgba(0,0,0,0.35), 0 24px 60px -12px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.06)',
				}}
			>
				{body}
			</div>
		</>
	);

};

/**
 * Mobile chat: full-width bottom drawer with drag-to-close on the grab handle.
 */
const MobileChatDrawer = ({ onClose, body }: { onClose: () => void; body: React.ReactNode }) => {
	const { keyboardHeight } = useVisualViewportInset();
	const [dragY, setDragY] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const startY = useRef(0);
	const pointerId = useRef<number | null>(null);
	const sheetRef = useRef<HTMLDivElement>(null);
	const grabRef = useRef<HTMLDivElement>(null);

	const endDrag = (dy: number) => {
		const height = sheetRef.current?.offsetHeight ?? 0;
		const shouldClose = dy > Math.min(140, height * 0.22);
		setIsDragging(false);
		pointerId.current = null;
		if (shouldClose) onClose();
		else setDragY(0);
	};

	const onPointerDown = (e: React.PointerEvent) => {
		startY.current = e.clientY;
		pointerId.current = e.pointerId;
		setIsDragging(true);
		try {
			grabRef.current?.setPointerCapture(e.pointerId);
		} catch {}
	};
	const onPointerMove = (e: React.PointerEvent) => {
		if (pointerId.current !== e.pointerId) return;
		const dy = e.clientY - startY.current;
		setDragY(Math.max(0, dy));
	};
	const onPointerEnd = (e: React.PointerEvent) => {
		if (pointerId.current !== e.pointerId) return;
		endDrag(Math.max(0, e.clientY - startY.current));
	};

	return (
		<>
			<button
				type="button"
				aria-label="Close chat"
				onClick={onClose}
				className="fixed inset-0 z-[59] bg-black/60 animate-in fade-in duration-150"
				style={{ opacity: isDragging ? Math.max(0.2, 1 - dragY / 400) : undefined }}
			/>
			<div
				ref={sheetRef}
				role="dialog"
				aria-label="Chat"
				className={cn(
					'fixed inset-x-0 bottom-0 z-[60]',
					'h-[92dvh] max-h-[92dvh]',
					'rounded-t-2xl overflow-hidden flex flex-col',
					'bg-chrome border-t border-white/[0.08]',
					'shadow-2xl shadow-black/50',
					!isDragging && 'animate-in slide-in-from-bottom duration-250'
				)}
				style={{
					paddingBottom: `calc(env(safe-area-inset-bottom) + ${keyboardHeight}px)`,
					transform: dragY ? `translateY(${dragY}px)` : undefined,
					transition: isDragging ? 'none' : 'transform 200ms ease-out',
				}}
			>
				<div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-white/15" />
				{/* Drag-to-close grab handle — wider hit area, pointer events for touch + mouse */}
				<div
					ref={grabRef}
					className="flex justify-center pt-3 pb-3 shrink-0 cursor-grab active:cursor-grabbing touch-none select-none"
					style={{ touchAction: 'none' }}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerEnd}
					onPointerCancel={onPointerEnd}
				>
					<div
						className="h-1.5 w-12 rounded-full bg-white/30"
						style={{
							transform: dragY ? `translateY(${Math.min(dragY * 0.15, 6)}px)` : undefined,
							transition: isDragging ? 'none' : 'transform 200ms ease-out',
						}}
					/>
				</div>
				{body}
			</div>
		</>
	);
};

export default ChatPanel;
